import type {
    CollectionState,
    CollectionAdapter,
    CollectionStateAdapter,
    CollectionSelectors,
    CollectionStateFactory,
    CollectionSelectorsFactory,
    CollectionStateStatuses,
} from './collections-adapter.type';
import { createSelector } from '@ngrx/store';

export enum CollectionStateStatus {
    idle = 'idle', // initial state
    loading = 'loading', // loading data
    success = 'success', // data loaded
    failure = 'failure', // error while loading data
}

export enum CollectionError {
    notFound = 'notFound',
    unauthorized = 'unauthorized',
    unknown = 'unknown',
}

// INITIAL STATE
function getInitialCollectionState<T>(): CollectionState<T> {
    return {
        items: null,
        status: CollectionStateStatus.idle,
        error: null,
    };
}

function createInitialCollectionStateFactory<T>(): CollectionStateFactory<T> {
    function getInitialState(): CollectionState<T>;
    function getInitialState<S extends object>(state: S): CollectionState<T> & S;
    function getInitialState(additionalState: any = {}): any {
        return Object.assign(getInitialCollectionState<T>(), additionalState);
    }

    return { getInitialState };
}
// END INITIAL STATE

// STATE ADAPTER
function createCollectionStateAdapter<T>(): CollectionStateAdapter<T> {
    function loadCollectionRequest(state: CollectionState<T>): CollectionState<T> {
        return {
            ...state,
            items: [],
            status: CollectionStateStatus.loading,
        };
    }

    function loadCollectionSuccess(items: T[], state: CollectionState<T>): CollectionState<T> {
        return {
            ...state,
            items,
            status: CollectionStateStatus.success,
        };
    }

    function loadCollectionFailure(error: string, state: CollectionState<T>): CollectionState<T> {
        return {
            ...state,
            status: CollectionStateStatus.failure,
            error,
        };
    }

    return {
        loadCollectionRequest,
        loadCollectionSuccess,
        loadCollectionFailure,
    } as CollectionStateAdapter<T>;
}
// END STATE ADAPTER

// SELECTORS
function createSelectorsFactory<T>(): CollectionSelectorsFactory<T> {
    function getSelectors(): CollectionSelectors<T> {
        const selectCollection = (state: CollectionState<T>): T[] => state.items;
        const selectCollectionStatus = (state: CollectionState<T>): CollectionStateStatus => state.status;

        const selectCollectionStatuses = createSelector(
            selectCollection,
            selectCollectionStatus,
            (items: T[], status: CollectionStateStatus): CollectionStateStatuses => ({
                isReady: status === CollectionStateStatus.success || status === CollectionStateStatus.failure,
                isNotReady: status === CollectionStateStatus.idle || status === CollectionStateStatus.loading,
                isEmpty: status === CollectionStateStatus.success && !items?.length,
                isNotEmpty: status === CollectionStateStatus.success && !!items?.length,
                isInitial: status === CollectionStateStatus.idle,
                isLoading: status === CollectionStateStatus.loading,
                isFailure: status === CollectionStateStatus.failure,
            })
        );

        return {
            selectCollection,
            selectCollectionStatus,
            selectCollectionStatuses,
        };
    }

    return { getSelectors };
}
// END SELECTORS

export function createCollectionAdapter<T>(): CollectionAdapter<T> {
    const stateFactory: CollectionStateFactory<T> = createInitialCollectionStateFactory<T>();
    const selectorsFactory: CollectionSelectorsFactory<T> = createSelectorsFactory<T>();
    const stateAdapter: CollectionStateAdapter<T> = createCollectionStateAdapter<T>();

    return {
        ...stateFactory,
        ...stateAdapter,
        ...selectorsFactory,
    };
}
